import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  FileText, Image, Video, File, Download, Layout, Grid, BookOpen,
  DollarSign, Presentation, FileSignature, RefreshCw, AlertCircle, FolderOpen
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROJECT_API = `${API}/project-catalog`;

// Category icons
const categoryIcons = {
  floor_plan: Layout,
  fact_sheet: FileText,
  unit_plan: Grid,
  brochure: BookOpen,
  price_list: DollarSign,
  site_photo: Image,
  video: Video,
  presentation: Presentation,
  contract: FileSignature,
  other: File,
};

// Category colors
const categoryColors = {
  floor_plan: 'bg-blue-500',
  fact_sheet: 'bg-green-500',
  unit_plan: 'bg-purple-500',
  brochure: 'bg-orange-500',
  price_list: 'bg-yellow-500',
  site_photo: 'bg-pink-500',
  video: 'bg-red-500',
  presentation: 'bg-indigo-500',
  contract: 'bg-slate-500',
  other: 'bg-gray-500',
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default function SharedDocumentView() {
  const { shareToken } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [document, setDocument] = useState(null);
  const [projectName, setProjectName] = useState('');
  
  // Fetch shared document
  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get(`${PROJECT_API}/shared/doc/${shareToken}`);
        
        if (response.data.success) {
          setDocument(response.data.document);
          setProjectName(response.data.project_name || 'Unknown Project');
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setError('This share link is invalid or has expired.');
        } else {
          setError('Failed to load shared document. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (shareToken) {
      fetchDocument();
    }
  }, [shareToken]);
  
  // Download document
  const handleDownload = async () => {
    try {
      const response = await axios.get(
        `${PROJECT_API}/shared/doc/${shareToken}/download`,
        { responseType: 'blob' }
      );
      
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', document?.file_name || 'download');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error('Failed to download document');
    }
  };
  
  const Icon = document ? (categoryIcons[document.category] || File) : File;
  const bgColor = document ? (categoryColors[document.category] || 'bg-gray-500') : 'bg-gray-500';
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 animate-spin text-accent mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-400">Loading shared document...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              Unable to Access
            </h2>
            <p className="text-slate-600 dark:text-slate-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center pb-4">
          <div className={`w-20 h-20 ${bgColor} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
            <Icon className="w-10 h-10 text-white" />
          </div>
          <CardTitle className="text-xl">{document?.file_name}</CardTitle>
          <div className="flex items-center justify-center gap-2 mt-2 text-sm text-slate-500">
            <FolderOpen className="w-4 h-4" />
            <span>{projectName}</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">File Size</p>
                <p className="font-medium">{formatFileSize(document?.file_size || 0)}</p>
              </div>
              <div>
                <p className="text-slate-500">Type</p>
                <p className="font-medium capitalize">
                  {(document?.category || 'other').replace('_', ' ')}
                </p>
              </div>
            </div>
            {document?.description && (
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <p className="text-slate-500 text-sm">Description</p>
                <p className="text-sm mt-1">{document.description}</p>
              </div>
            )}
          </div>
          
          <Button className="w-full" size="lg" onClick={handleDownload}>
            <Download className="w-5 h-5 mr-2" />
            Download Document
          </Button>
        </CardContent>
      </Card>
      
      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-100 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
        <div className="max-w-6xl mx-auto px-4 py-4 text-center">
          <p className="text-sm text-slate-500">
            Shared via Treppan CRM
          </p>
        </div>
      </div>
    </div>
  );
}
